import React, { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import axios from "axios";
import moment from "moment";
import Links from "../../Data/Links";
import { MONTHS, YEARS } from "../../constants";

export default function AdsReport() {
  const currentYear = moment().format("YYYY");
  const currentMonth = moment().format("MMM");

  const [month, setMonth] = useState(currentMonth);
  const [year, setYear] = useState(currentYear);
  const [storeName, setStoreName] = useState("");
  const [storeId, setStoreId] = useState("");
  const [division, setDivision] = useState("");
  const [source, setSource] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return num.toLocaleString();
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return `${(num * 100).toFixed(2)}%`;
  };

  const toNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const getMetricValue = (row, keys, defaultValue = undefined) => {
    if (!row) return defaultValue;
    for (const key of keys) {
      if (row[key] !== undefined && row[key] !== null && row[key] !== "") {
        return row[key];
      }
    }
    return defaultValue;
  };

  const getStoreName = (row) => {
    return (
      row?.ACCOUNT_NAME ||
      row?.account_name ||
      row?.store_name ||
      row?.store ||
      row?.dealership ||
      row?.name ||
      row?.storeName ||
      row?.store_id ||
      row?.id ||
      "-"
    );
  };

  const getCampaignName = (row) => {
    return row?.CAMPAIGN_NAME || row?.campaign || row?.campaign_name || row?.campaignName || row?.adset || row?.ad_set || "-";
  };

  const endpoints = useMemo(
    () => ({
      google: Links.googleAdsReport,
      meta: Links.metaAdsReport
    }),
    []
  );

  const fetchAds = () => {

    setLoading(true);
    setError("");

    const startDate = moment(`${year}-${month}-01`, "YYYY-MMM-DD").startOf("month");
    const isCurrentMonth = year === moment().format("YYYY") && month === moment().format("MMM");
    const endDate = isCurrentMonth
      ? moment()
      : moment(`${year}-${month}-01`, "YYYY-MMM-DD").endOf("month");
    const payload = {
      start_date: startDate.format("YYYY-MM-DD"),
      end_date: endDate.format("YYYY-MM-DD")
    };

    if (storeName.trim()) payload.store_name = storeName.trim();
    if (division.trim()) payload.division = division.trim();
    if (storeId !== "" && !Number.isNaN(Number(storeId))) {
      payload.store_id = Number(storeId);
    }

    const requests = [];
    if (source === "all" || source === "google") {
      requests.push(axios.post(endpoints.google, payload).then((res) => ({ source: "Google", data: res.data?.data || [] })));
    }
    if (source === "all" || source === "meta") {
      requests.push(axios.post(endpoints.meta, payload).then((res) => ({ source: "Meta", data: res.data?.data || [] })));
    }

    axios
      .all(requests)
      .then((results) => {
        const merged = results.flatMap((result) =>
          (result.data || []).map((row) => ({ ...row, __source: result.source }))
        );
        setRows(merged);
      })
      .catch((err) => {
        console.log("Ads report fetch error:", err);
        setRows([]);
        setError("Failed to load ads report data.");
      })
      .finally(() => setLoading(false));
  };

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.impressions += toNumber(getMetricValue(row, ["IMPRESSIONS", "impressions", "impression"]));
        acc.clicks += toNumber(getMetricValue(row, ["CLICKS", "clicks", "click"]));
        acc.spend += toNumber(getMetricValue(row, ["SPEND", "spend", "cost", "amount_spent"]));
        acc.leads += toNumber(getMetricValue(row, ["LEADS", "leads", "lead"]));
        acc.conversions += toNumber(getMetricValue(row, ["CONVERSIONS", "conversions", "conversion"]));
        acc.conversionValue += toNumber(getMetricValue(row, ["CONVERSIONS_VALUE", "conversions_value"]));
        return acc;
      },
      { impressions: 0, clicks: 0, spend: 0, leads: 0, conversions: 0, conversionValue: 0 }
    );
  }, [rows]);

  const totalCtr = totals.impressions ? totals.clicks / totals.impressions : 0;
  const totalCpc = totals.clicks ? totals.spend / totals.clicks : 0;
  const totalCpl = totals.leads ? totals.spend / totals.leads : 0;
  const totalCpa = totals.conversions ? totals.spend / totals.conversions : 0;

  useEffect(() => {
    fetchAds();
  }, []);

  return (
    <div style={{ padding: "24px" }}>
      <h2>Google & Meta Ads Report</h2>

      <Grid container spacing={2} alignItems="center" style={{ marginBottom: 16 }}>
        <Grid item xs={12} sm={4} md={2.4}>
          <FormControl fullWidth>
            <InputLabel id="ads-month-label">Month</InputLabel>
            <Select
              labelId="ads-month-label"
              value={month}
              label="Month"
              onChange={(event) => setMonth(event.target.value)}
            >
              {Object.values(MONTHS).map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4} md={2.4}>
          <FormControl fullWidth>
            <InputLabel id="ads-year-label">Year</InputLabel>
            <Select
              labelId="ads-year-label"
              value={year}
              label="Year"
              onChange={(event) => setYear(event.target.value)}
            >
              {YEARS.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4} md={2.4}>
          <TextField
            fullWidth
            label="Store Name"
            value={storeName}
            onChange={(event) => setStoreName(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={2.4}>
          <TextField
            fullWidth
            label="Store ID"
            value={storeId}
            onChange={(event) => setStoreId(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={2.4}>
          <TextField
            fullWidth
            label="Division"
            value={division}
            onChange={(event) => setDivision(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={2.4}>
          <Button
            variant="contained"
            color="primary"
            onClick={fetchAds}
            disabled={loading}
          >
            {loading ? "Loading..." : "Load"}
          </Button>
        </Grid>
      </Grid>

      <ToggleButtonGroup
        color="primary"
        value={source}
        exclusive
        onChange={(event, nextValue) => {
          if (nextValue) setSource(nextValue);
        }}
        style={{ marginBottom: 16 }}
      >
        <ToggleButton value="all">All</ToggleButton>
        <ToggleButton value="google">Google Ads</ToggleButton>
        <ToggleButton value="meta">Meta Ads</ToggleButton>
      </ToggleButtonGroup>

      {error && (
        <div style={{ color: "#b00020", marginBottom: 16 }}>
          {error}
        </div>
      )}

      <Grid container spacing={2} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #0d47a1", background: "#eef4ff" }}>
            <div className="text-muted">Spend</div>
            <div className="h5 mb-0">{formatCurrency(totals.spend)}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #00796b", background: "#f2fbf9" }}>
            <div className="text-muted">Impressions</div>
            <div className="h5 mb-0">{formatNumber(totals.impressions)}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #5d4037", background: "#fbf4f2" }}>
            <div className="text-muted">Clicks</div>
            <div className="h5 mb-0">{formatNumber(totals.clicks)}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #1565c0", background: "#f2f6fb" }}>
            <div className="text-muted">CTR</div>
            <div className="h5 mb-0">{formatPercent(totalCtr)}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #ff8f00", background: "#fff8e1" }}>
            <div className="text-muted">CPC</div>
            <div className="h5 mb-0">{formatCurrency(totalCpc)}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #00838f", background: "#e0f7fa" }}>
            <div className="text-muted">Leads</div>
            <div className="h5 mb-0">{formatNumber(totals.leads)}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #6a1b9a", background: "#faf3ff" }}>
            <div className="text-muted">CPL</div>
            <div className="h5 mb-0">{formatCurrency(totalCpl)}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #455a64", background: "#f3f5f7" }}>
            <div className="text-muted">Conversions</div>
            <div className="h5 mb-0">{formatNumber(totals.conversions)}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #2e7d32", background: "#f1fbf4" }}>
            <div className="text-muted">CPA</div>
            <div className="h5 mb-0">{formatCurrency(totalCpa)}</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <div className="card p-2" style={{ borderTop: "4px solid #b00020", background: "#fdecea" }}>
            <div className="text-muted">Conversion Value</div>
            <div className="h5 mb-0">{formatCurrency(totals.conversionValue)}</div>
          </div>
        </Grid>
      </Grid>

      <div className="table-responsive">
        <table className="table table-striped table-bordered">
          <thead>
            <tr style={{ background: "#1f2a44", color: "#ffffff" }}>
              <th>Source</th>
              <th>Store</th>
              <th>Campaign</th>
              <th>Impressions</th>
              <th>Clicks</th>
              <th>CTR</th>
              <th>Spend</th>
              <th>CPC</th>
              <th>Leads</th>
              <th>Conversions</th>
              <th>Conversion Value</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading ? (
              <tr>
                <td colSpan={11} style={{ textAlign: "center" }}>
                  No data found.
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={row?.id || row?.campaign_id || index}>
                  <td>{row.__source || (source === "meta" ? "Meta" : "Google")}</td>
                  <td>{getStoreName(row)}</td>
                  <td>{getCampaignName(row)}</td>
                  <td>{formatNumber(getMetricValue(row, ["IMPRESSIONS", "impressions", "impression"]))}</td>
                  <td>{formatNumber(getMetricValue(row, ["CLICKS", "clicks", "click"]))}</td>
                  <td>{formatPercent(getMetricValue(row, ["CTR", "ctr"]))}</td>
                  <td>{formatCurrency(getMetricValue(row, ["SPEND", "spend", "cost", "amount_spent"]))}</td>
                  <td>{formatCurrency(getMetricValue(row, ["CPC", "cpc"]))}</td>
                  <td>{formatNumber(getMetricValue(row, ["LEADS", "leads", "lead"]))}</td>
                  <td>{formatNumber(getMetricValue(row, ["CONVERSIONS", "conversions", "conversion"]))}</td>
                  <td>{formatCurrency(getMetricValue(row, ["CONVERSIONS_VALUE", "conversions_value"]))}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
